import { SubmitFormDialog } from '@shared/components/dialogs/submit-form-dialog';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import {
  AddProcedureStockAllowanceType,
  getProcedureInvalidation,
  mapDefaultStockAlowances,
  PostNewProcedure,
} from '../entity';
import { useDeleteProcedureMutation, useProceduresMutation } from '../queries';
import { useEditableAndReadonlyStockAllowances } from '../store';
import AddSaleItemsForm from './AddSaleItemsForm';
import { useRef } from 'react';
import ProcedureCard from './ProcedureCard';
import { StockAllowance } from '../../entity';
import { queryClient } from '@shared/queries';
import { useSuspenseStocksQuery } from '../../queries';

export type AddSaleItemStockAllowanceType = (Omit<StockAllowance, 'id' | 'quantity'> & {
  stockAllowanceId: string;
  quantity: number | null;
  stockItemName?: string;
  avgUnitPrice?: string;
  costs?: number;
  totalPrice: number;
  type?: 'SALE' | 'CONSUMPTION';
})[];

export type SaleItemsFormValues = {
  stockAllowances: AddSaleItemStockAllowanceType;
};

export type ProcedureSalesFormDialogProps = {
  defaultValues?: {
    stockAllowances: AddSaleItemStockAllowanceType;
  };
  procedureId?: string;
  isVisitClosed: boolean;
  procedures: Array<{
    id?: string;
    stepOrder: number;
    type: 'SALE' | 'CONSUMPTION';
    description?: string | null;
    stockAllowances: AddProcedureStockAllowanceType;
  }>;

  dialogRef?: React.RefObject<ImperativeFormDialogRefType | null>;
};

const ProcedureSalesFormDialog = (props: ProcedureSalesFormDialogProps) => {
  const { t } = useIntlTranslations();
  const internalDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const {
    defaultValues,
    procedureId,
    isVisitClosed,
    dialogRef: externalDialogRef,
    procedures,
  } = props;
  const procedureSalesFormDialogRef = externalDialogRef ?? internalDialogRef;
  const { editableAllowances, readonlyAllowances } = useEditableAndReadonlyStockAllowances(
    defaultValues?.stockAllowances,
  );
  const { visitId, clientId } = useParams();
  const { data: stocks } = useSuspenseStocksQuery();

  const methods = useForm<SaleItemsFormValues>({
    defaultValues: {
      stockAllowances: mapDefaultStockAlowances(editableAllowances).map(item => ({
        ...item,
        type: 'SALE',
      })),
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: 'stockAllowances',
    control: methods.control,
  });

  const { mutation: deleteProcedure } = useDeleteProcedureMutation({
    onSuccess: () => {
      getProcedureInvalidation(stocks, visitId, clientId);
    },
  });
  const hasNoSoldItems = procedures.length === 0;
  const { mutation: createNewProcedure } = useProceduresMutation({
    onSuccess: () => {
      getProcedureInvalidation(stocks, visitId, clientId);
      methods.reset();
      procedureSalesFormDialogRef.current?.close();
      queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });

  // const handleClickOpen = () => {
  //   if (defaultValues) {
  //     const stockAllowancesDefault = mapDefaultStockAlowances(editableAllowances).map(item => ({
  //       ...item,
  //       type: 'SALE' as const,
  //     }));
  //     replace(stockAllowancesDefault);
  //   }

  //   procedureSalesFormDialogRef.current?.open();
  // };

  // const handleClose = () => {
  //   procedureSalesFormDialogRef.current?.close();
  // };

  return (
    <>
      {!externalDialogRef &&
        (!hasNoSoldItems || (
          <Button
            variant="outlined"
            disabled={isVisitClosed}
            onClick={() => procedureSalesFormDialogRef.current?.open()}
            sx={{
              boxShadow: '0px 0px 6px 2px rgba(0,0,0,0.15)',
              borderColor: 'success.main',
              color: '#259728',
              '&:hover': {
                borderColor: 'success.dark',
                backgroundColor: 'success.light',
              },
            }}
          >
            {t('visitDetail.addSaleItemsButton')}
          </Button>
        ))}
      <SubmitFormDialog
        methods={methods}
        ref={procedureSalesFormDialogRef}
        onOpen={() =>
          methods.reset({
            stockAllowances: mapDefaultStockAlowances(editableAllowances).map(item => ({
              ...item,
              type: 'SALE' as const,
            })),
          })
        }
        onSubmit={formData => {
          const saleItems = formData.stockAllowances.map(item => ({
            ...item,
            quantity: Number(item.quantity),
            type: 'SALE' as const,
          }));

          createNewProcedure.mutate({
            stockAllowances: [...saleItems, ...readonlyAllowances],
            type: 'SALE',
            description: t('formDialogAddSaleItems.defaultDescription'),
            visitId,
            id: procedureId,
          } as unknown as PostNewProcedure);
          procedureSalesFormDialogRef.current?.close();
          scroll();
        }}
        actions={
          <>
            {procedureId && (
              <Button
                onClick={() => deleteProcedure.mutate(procedureId)}
                endIcon={<DeleteOutlineIcon fontSize="small" />}
              >
                <FormattedMessage id="formDialog.delete" defaultMessage="Smazat" />
              </Button>
            )}
          </>
        }
        title={t('formDialogAddSaleItems.title')}
        dialogHelperText={t('formDialogAddSaleItems.helperText')}
      >
        <AddSaleItemsForm
          control={methods.control}
          name="stockAllowances"
          remove={remove}
          fields={fields}
          append={append}
          readonlyAllowances={readonlyAllowances}
        />
      </SubmitFormDialog>
      {procedures &&
        procedures.map(procedure => (
          <ProcedureCard
            key={procedure.id}
            disabled={isVisitClosed}
            procedureId={procedure.id}
            orderNumber={procedure.stepOrder}
            type={procedure.type}
            stockAllowances={procedure.stockAllowances}
          />
        ))}
    </>
  );
};

export default ProcedureSalesFormDialog;
